/**
===================================================
Module: frontend.src.components.ChangePassword.tsx
Description: Mandatory password change after signing in with an invitation password
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ApiError, apiService } from "../services/api";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/CompleteProfile.css";

const MIN_LENGTH = 8;

const ChangePassword: React.FC = () => {
  const { t } = useTranslation("tenant");
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const notify = (type: Notification["type"], message: string) =>
    setNotifications((prev) => [
      ...prev,
      { id: Date.now().toString(), type, message },
    ]);

  const removeNotification = (id: string) =>
    setNotifications((prev) => prev.filter((n) => n.id !== id));

  const mandatory = user?.password_change_required === true;
  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit =
    current.length > 0 &&
    next.length >= MIN_LENGTH &&
    next === confirm &&
    next !== current &&
    !isSaving;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSaving(true);
    try {
      await apiService.changeOwnPassword(current, next);
      await refreshUser();
      notify("success", t("password.success"));
      navigate("/projects", { replace: true });
    } catch (error) {
      const code = error instanceof ApiError ? error.code : undefined;
      notify(
        "error",
        code === "PASSWORD_INCORRECT"
          ? t("password.incorrect")
          : error instanceof Error
            ? error.message
            : t("errors.generic"),
      );
      setIsSaving(false);
    }
  };

  return (
    <div className="profile-setup">
      <div className="profile-card">
        <div className="profile-head">
          <h1>{t("password.title")}</h1>
          <p>{mandatory ? t("password.introMandatory") : t("password.intro")}</p>
          {user?.email && <p className="profile-email">{user.email}</p>}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label htmlFor="pw-current">
              {t("password.current")} <span className="req">*</span>
            </label>
            <input
              id="pw-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
            />
            {mandatory && (
              <span className="field-hint">{t("password.currentHint")}</span>
            )}
          </div>

          <div className="field">
            <label htmlFor="pw-new">
              {t("password.new")} <span className="req">*</span>
            </label>
            <input
              id="pw-new"
              type="password"
              autoComplete="new-password"
              minLength={MIN_LENGTH}
              value={next}
              className={tooShort || sameAsCurrent ? "invalid" : ""}
              onChange={(e) => setNext(e.target.value)}
              required
            />
            {tooShort && (
              <span className="field-error">
                {t("password.tooShort", { count: MIN_LENGTH })}
              </span>
            )}
            {sameAsCurrent && (
              <span className="field-error">{t("password.sameAsCurrent")}</span>
            )}
          </div>

          <div className="field">
            <label htmlFor="pw-confirm">
              {t("password.confirm")} <span className="req">*</span>
            </label>
            <input
              id="pw-confirm"
              type="password"
              autoComplete="new-password"
              minLength={MIN_LENGTH}
              value={confirm}
              className={mismatch ? "invalid" : ""}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            {mismatch && (
              <span className="field-error">{t("password.mismatch")}</span>
            )}
          </div>

          <div className="profile-actions">
            <button type="submit" className="btn-primary" disabled={!canSubmit}>
              {isSaving ? t("password.saving") : t("password.submit")}
            </button>
            {mandatory ? (
              <button type="button" className="link-btn" onClick={logout}>
                {t("profile.signOut")}
              </button>
            ) : (
              <button
                type="button"
                className="link-btn"
                onClick={() => navigate(-1)}
              >
                {t("common.cancel")}
              </button>
            )}
          </div>
        </form>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default ChangePassword;
